// src/components/OccasionLandingPage.jsx
// SEO-optimized landing pages for each greeting card occasion
// Dynamically pulls categories from the templates API

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { listTemplates, listCategoryTemplates, getSubscriptionStatus, getDrafts } from '../utils/api';
import { useAuthContext } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { getOccasionData, categoryToSlug, slugToDisplayName } from '../utils/occasions';
import SEO, { createBreadcrumbData, createOccasionProductData } from './SEO';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/lottie/loading.json';

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="w-12 h-12">
      <Lottie animationData={loadingAnimation} loop={true} autoplay={true} />
    </div>
  );
}

// Check if a template filename is a "favorite" (should stay at top, not shuffled)
function isFavoriteTemplate(filename) {
  const favoritePrefix = ['0_fav_', '0_0-', '0_1-', '0_2-'];
  return favoritePrefix.some(prefix => filename.startsWith(prefix));
}

// Fisher-Yates shuffle algorithm to randomize template order
// Favorites (0_fav_, 0_0-, 0_1-, 0_2- prefixed) stay at top in original order
function shuffleArray(array) {
  // Separate favorites from regular templates
  const favorites = array.filter(isFavoriteTemplate);
  const regular = array.filter(f => !isFavoriteTemplate(f));
  
  // Only shuffle regular templates
  const shuffled = [...regular];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  // Return favorites first (in original order), then shuffled regular templates
  return [...favorites, ...shuffled];
}

// Helper to check if a template is a video
function isVideoTemplate(filename) {
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
  return videoExtensions.some(ext => filename.toLowerCase().endsWith(ext));
}

// Template card component - handles both images and videos
function TemplateCard({ template, category, onSelect, templateInfo }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.1.165:8000';
  
  // Use Firebase URL if available (for videos), otherwise use local API
  const src = templateInfo?.url && templateInfo?.source === 'firebase' 
    ? templateInfo.url 
    : `${baseUrl}/templates/${category}/${template}`;
  
  const isVideo = templateInfo?.is_video || templateInfo?.media_type === 'video' || isVideoTemplate(template);

  // For Safari: append #t=0.001 to show first frame immediately
  const videoSrc = isVideo ? `${src}#t=0.001` : src;

  return (
    <button
      onClick={() => onSelect(template)}
      className={`group relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300 aspect-[3/4] ${isVideo ? 'bg-gray-900' : 'bg-white'}`}
    >
      {!isLoaded && (
        <div className={`absolute inset-0 flex items-center justify-center ${isVideo ? 'bg-gray-900' : 'bg-gray-100'}`}>
          <div className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-indigo-500 animate-spin" />
        </div>
      )}
      
      {isVideo ? (
        <>
          <video
            src={videoSrc}
            muted
            loop
            playsInline
            autoPlay
            webkit-playsinline="true"
            preload="auto"
            crossOrigin="anonymous"
            className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onCanPlay={() => setIsLoaded(true)}
            onLoadedData={() => setIsLoaded(true)}
          />
          {/* Video indicator badge */}
          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-10">
            <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <span>Video</span>
          </div>
        </>
      ) : (
        <img
          src={src}
          alt={`${category} greeting card template`}
          className={`w-full h-full object-contain transition-all duration-300 group-hover:scale-105 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setIsLoaded(true)}
          loading="lazy"
        />
      )}
      
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute bottom-4 left-4 right-4">
          <span className="text-white font-medium text-sm">
            {isVideo ? 'Use this video template →' : 'Customize this template →'}
          </span>
        </div>
      </div>
    </button>
  );
}

export default function OccasionLandingPage() {
  const { occasion: occasionSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { showError } = useError();
  
  const [templates, setTemplates] = useState([]);
  const [templatesInfo, setTemplatesInfo] = useState({}); // Map of filename -> template info (for video detection)
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [occasion, setOccasion] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [notFound, setNotFound] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [occasionSlug]);

  // Fetch categories and match the slug to a real category
  useEffect(() => {
    async function fetchAndMatchCategory() {
      setCategoryLoading(true);
      setNotFound(false);
      
      try {
        const { data } = await listTemplates();
        const categories = Object.keys(data);
        setAllCategories(categories);
        
        // Find matching category (case-insensitive, slug-based matching)
        const matchedCategory = categories.find(cat => {
          const catSlug = categoryToSlug(cat);
          return catSlug === occasionSlug || cat.toLowerCase() === occasionSlug.toLowerCase();
        });
        
        if (matchedCategory) {
          // Build occasion data from the matched category
          const categoryIndex = categories.indexOf(matchedCategory);
          const occasionData = getOccasionData(matchedCategory, categoryIndex);
          setOccasion(occasionData);
        } else {
          // Try to see if the slug could be a partial match
          const partialMatch = categories.find(cat => 
            cat.toLowerCase().includes(occasionSlug.replace(/-/g, ' ')) ||
            occasionSlug.replace(/-/g, ' ').includes(cat.toLowerCase())
          );
          
          if (partialMatch) {
            const categoryIndex = categories.indexOf(partialMatch);
            const occasionData = getOccasionData(partialMatch, categoryIndex);
            setOccasion(occasionData);
          } else {
            setNotFound(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setNotFound(true);
      } finally {
        setCategoryLoading(false);
      }
    }
    
    fetchAndMatchCategory();
  }, [occasionSlug]);

  // Load templates once we have the occasion
  useEffect(() => {
    async function loadTemplates() {
      if (!occasion) return;
      
      setLoading(true);
      try {
        const { data } = await listCategoryTemplates(occasion.category, 1, 12);
        setTemplates(shuffleArray(data.templates || [])); // Shuffle for variety on each visit
        setHasMore(data.has_more || false);
        setPage(1);
        
        // Store templates_info for video detection and Firebase URLs
        if (data.templates_info) {
          const infoMap = {};
          data.templates_info.forEach(info => {
            infoMap[info.filename] = info;
          });
          setTemplatesInfo(infoMap);
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setLoading(false);
      }
    }
    loadTemplates();
  }, [occasion]);

  // Load more templates
  const loadMore = useCallback(async () => {
    if (!occasion || !hasMore) return;
    
    try {
      const nextPage = page + 1;
      const { data } = await listCategoryTemplates(occasion.category, nextPage, 12);
      setTemplates(prev => [...prev, ...(data.templates || [])]);
      setHasMore(data.has_more || false);
      setPage(nextPage);
      
      // Merge new templates_info for video detection
      if (data.templates_info) {
        setTemplatesInfo(prev => {
          const newInfo = { ...prev };
          data.templates_info.forEach(info => {
            newInfo[info.filename] = info;
          });
          return newInfo;
        });
      }
    } catch (error) {
      console.error('Failed to load more templates:', error);
    }
  }, [occasion, page, hasMore]);

  // Handle template selection
  const handleTemplateSelect = async (filename) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.1.165:8000';
    const templateId = filename.replace(/[^0-9]/g, '') || '1';
    
    // Get template info for video detection and Firebase URL
    const templateInfo = templatesInfo[filename];
    const isVideo = templateInfo?.is_video || templateInfo?.media_type === 'video' || isVideoTemplate(filename);
    
    // Use Firebase URL if available (for videos), otherwise use local API
    const src = templateInfo?.url && templateInfo?.source === 'firebase' 
      ? templateInfo.url 
      : `${baseUrl}/templates/${occasion.category}/${filename}`;
    
    const mediaType = isVideo ? 'video' : 'image';
    const source = templateInfo?.source || 'local';

    // Common template data for navigation state
    const templateData = { 
      id: templateId, 
      src, 
      category: occasion.category, 
      cardSize: '1024x1024',
      mediaType,
      isVideo,
      source,
      firebaseUrl: templateInfo?.url || null
    };

    // Check authentication and subscription
    if (!user) {
      const redirectPath = `/edit/${occasion.category}/${templateId}`;
      navigate('/register?redirect=' + encodeURIComponent(redirectPath), {
        state: {
          fromTemplate: true,
          message: 'Create a free account to customize this template!',
          templateData,
        },
      });
      return;
    }

    let statusData;
    try {
      const response = await getSubscriptionStatus();
      statusData = response.data;
      if (!statusData.active || !statusData.subscription_data) {
        navigate('/subscribe', {
          state: {
            fromTemplate: true,
            templateData,
          },
        });
        return;
      }
    } catch {
      navigate('/subscribe', {
        state: {
          fromTemplate: true,
          templateData,
        },
      });
      return;
    }

    // Check draft limit before proceeding (only for plans with limited drafts)
    const draftLimit = statusData?.limits?.drafts_per_user;
    // Only check if limit is a finite number (not unlimited/-1)
    if (typeof draftLimit === 'number' && draftLimit > 0 && draftLimit < 999999) {
      try {
        const { data: drafts } = await getDrafts();
        const currentDraftCount = Array.isArray(drafts) ? drafts.length : 0;
        
        if (currentDraftCount >= draftLimit) {
          // User has reached their draft limit - show upgrade modal with positive messaging
          showError(
            `You've created your free preview card! 🎨 Upgrade to create unlimited cards and send them to loved ones.`,
            true // requiresUpgrade = true to show upgrade modal
          );
          return;
        }
      } catch (err) {
        // If we can't check drafts, log but don't block (let save_draft handle it)
        console.warn("Could not check draft count:", err);
      }
    }

    // Navigate to editor
    navigate(`/edit/${occasion.category}/${templateId}`, {
      state: { 
        src, 
        category: occasion.category, 
        cardSize: '1024x1024',
        mediaType,
        isVideo,
        source,
        firebaseUrl: templateInfo?.url || null
      },
    });
  };

  // Loading state
  if (categoryLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
        <span className="ml-4 text-gray-600">Loading...</span>
      </div>
    );
  }

  // 404 for unknown occasions
  if (notFound || !occasion) {
    // Build suggestions from available categories
    const suggestions = allCategories.slice(0, 6).map((cat, index) => {
      const data = getOccasionData(cat, index);
      return data;
    });
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Occasion Not Found</h1>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            We couldn't find templates for "{slugToDisplayName(occasionSlug)}". 
            Try one of our available categories:
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {suggestions.map(occ => (
              <Link
                key={occ.slug}
                to={`/occasions/${occ.slug}`}
                className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 transition"
              >
                {occ.emoji} {occ.name}
              </Link>
            ))}
          </div>
          <Link
            to="/occasions"
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            View all occasions →
          </Link>
        </div>
      </div>
    );
  }

  // Structured data for this occasion
  const breadcrumbData = createBreadcrumbData([
    { name: 'Home', path: '/' },
    { name: 'Occasions', path: '/occasions' },
    { name: occasion.name, path: `/occasions/${occasion.slug}` },
  ]);

  const productData = createOccasionProductData(occasion);

  return (
    <>
      <SEO
        title={`${occasion.name} Greeting Cards`}
        description={occasion.metaDescription}
        path={`/occasions/${occasion.slug}`}
        structuredData={[breadcrumbData, productData]}
      />

      {/* Hero Section */}
      <section className={`relative bg-gradient-to-br ${occasion.gradient} text-white py-16 md:py-24 overflow-hidden`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <span className="text-6xl md:text-7xl mb-4 block">{occasion.emoji}</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            {occasion.heroTitle}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
            {occasion.heroSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => document.getElementById('templates')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Browse Templates
            </button>
            <Link
              to="/"
              className="px-8 py-4 bg-white/20 text-white border-2 border-white/50 rounded-full font-bold text-lg hover:bg-white/30 transition-all"
            >
              Create with AI ✨
            </Link>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            Create Perfect {occasion.name} Cards
          </h2>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-12">
            {occasion.description}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {occasion.features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                <span className="text-4xl mb-4 block">{feature.icon}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section id="templates" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            {occasion.name} Card Templates
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Choose a template and customize it in minutes
          </p>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <LoadingSpinner />
              <span className="ml-4 text-gray-600">Loading templates...</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600 mb-4">No templates available for this occasion yet.</p>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-full font-semibold hover:bg-indigo-700 transition"
              >
                Create with AI Instead →
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {templates.map((template, index) => (
                  <TemplateCard
                    key={`${template}-${index}`}
                    template={template}
                    category={occasion.category}
                    onSelect={handleTemplateSelect}
                    templateInfo={templatesInfo[template]}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-10">
                  <button
                    onClick={loadMore}
                    className="px-8 py-3 bg-indigo-600 text-white rounded-full font-semibold hover:bg-indigo-700 transition"
                  >
                    Load More Templates
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Message Ideas Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            {occasion.name} Message Ideas
          </h2>
          <p className="text-xl text-gray-600 text-center mb-10">
            Not sure what to write? Here are some ideas to inspire you
          </p>

          <div className="grid gap-4">
            {occasion.messageIdeas.map((message, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 border-l-4 border-indigo-500 hover:shadow-md transition-shadow"
              >
                <p className="text-gray-700 text-lg italic">"{message}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            How to Create Your {occasion.name} Card
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Choose a Template', description: 'Browse our collection and pick a design you love' },
              { step: '2', title: 'Customize', description: 'Add your photos, text, and personal touches' },
              { step: '3', title: 'Preview', description: 'See exactly how your card will look' },
              { step: '4', title: 'Send', description: 'Share via link or email instantly' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-indigo-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 bg-gradient-to-r ${occasion.gradient}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Create Your {occasion.name} Card?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Start your 3-day free trial and access all templates and AI features
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => document.getElementById('templates')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Start Creating Now
            </button>
            <Link
              to="/subscribe"
              className="px-8 py-4 bg-white/20 text-white border-2 border-white/50 rounded-full font-bold text-lg hover:bg-white/30 transition-all"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Other Occasions Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8">
            Explore Other Occasions
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {allCategories
              .filter(cat => categoryToSlug(cat) !== occasion.slug)
              .slice(0, 8)
              .map((cat, index) => {
                const data = getOccasionData(cat, index);
                return (
                  <Link
                    key={data.slug}
                    to={`/occasions/${data.slug}`}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition font-medium"
                  >
                    {data.emoji} {data.name}
                  </Link>
                );
              })}
          </div>
          <div className="text-center mt-6">
            <Link
              to="/occasions"
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              View all occasions →
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}


